/**
 * Ecrã 0 — A Entrada Imersiva ("Identidade Digital do Comprador").
 * Substitui a landing como rota `/`. Uma pergunta, duas escolhas, acessos
 * discretos no rodapé. A escolha abre o chat DNA anónimo com a intenção.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Repeat, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { applyLandingMetadata } from './landing/landingSeo';
import MidLockup from '../components/MidLockup';
import { NEW_BRAND } from '../lib/brand';
import '../components/buyer_dna_v2/dna_brand.css';
// Zonamento: this dark entry screen follows night (DESIGN.md); .ck-portal is the shared night token scope.
const NIGHT = NEW_BRAND ? ' ck-portal entry-night' : '';

export default function EntryScreen() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  // Splash de arranque (manual variante A): mostra o lockup + barra de
  // progresso uma vez por sessão, antes da entrada.
  const [showSplash, setShowSplash] = useState(
    () => typeof window !== 'undefined' && !isAuthenticated && !sessionStorage.getItem('mid_splash_seen')
  );
  const endSplash = useCallback(() => {
    sessionStorage.setItem('mid_splash_seen', '1');
    setShowSplash(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'buyer') navigate('/buyer/dashboard', { replace: true });
    else if (isAuthenticated && user?.user_type === 'agent') navigate('/agent/dashboard', { replace: true });
    else if (isAuthenticated && user?.user_type === 'admin') navigate('/admin', { replace: true });
  }, [isAuthenticated, user, navigate]);

  useEffect(() => applyLandingMetadata('/', false), []);

  return (
    <>
    <AnimatePresence>
      {showSplash && <Splash key="splash" duration={2200} onDone={endSplash} />}
    </AnimatePresence>
    <div
      data-testid="entry-screen"
      className={`min-h-screen flex flex-col${NIGHT}`}
      style={{
        background: 'var(--brand-paper, radial-gradient(ellipse 80% 60% at 50% -10%, rgba(167,139,217,0.14), transparent), #0a0c14)',
        fontFamily: "var(--brand-font-body, 'Geist', 'Inter', system-ui, sans-serif)",
      }}
    >
      {/* Conteúdo central */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        {/* Lockup GRANDE — a mascote grande é o «i», com o m e o D ao lado
            para completar «miD». (Ecrã de arranque, manual variante A.) */}
        <motion.div
          initial={{ opacity: 1, scale: 0.94, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center gap-4"
          data-testid="entry-hero"
        >
          <MidLockup
            variant="vivo"
            fontSize={92}
            color="#FFFFFF"
            dColor="#A78BD9"
            data-testid="entry-lockup"
          />
          <span
            style={{
              fontSize: 12,
              letterSpacing: '0.26em',
              textTransform: 'uppercase',
              color: 'var(--brand-muted, rgba(255,255,255,0.7))',
              fontWeight: 500,
            }}
            data-testid="entry-descriptor"
          >
            Mediador Imobiliário Digital
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 1, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="mt-16 text-4xl sm:text-5xl font-bold text-white text-center tracking-tight leading-[1.05]"
          data-testid="entry-title"
        >
          Vais comprar casa em Portugal?
        </motion.h1>
        <motion.p
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mt-4 max-w-xl text-base md:text-lg text-center font-light"
          style={{ color: 'var(--brand-muted, rgba(255,255,255,0.7))' }}
        >
          Cria o teu Buyer DNA, compara anúncios e prepara a compra com o miD. Gratuito para compradores. Começa sem criar conta.
        </motion.p>

        {/* As duas escolhas */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
          <motion.a
            initial={{ opacity: 1, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.45 }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.98 }}
            href="/comecar?intent=primeira_habitacao"
            data-testid="entry-choice-primeira"
            className="group text-left rounded-2xl p-7 border transition-colors"
            style={{
              background: 'var(--brand-surface, rgba(167,139,217,0.10))',
              borderColor: 'var(--brand-line, rgba(167,139,217,0.35))',
            }}
          >
            <div className="flex items-center justify-between">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--brand-surface-2, rgba(167,139,217,0.22))' }}>
                <Home size={22} style={{ color: 'var(--brand-primary, #A78BD9)' }} />
              </div>
              <ArrowRight size={18} className="opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ color: 'var(--brand-primary, #A78BD9)' }} />
            </div>
            <div className="mt-5 text-xl font-semibold text-white">Primeira casa</div>
            <div className="mt-1 text-sm" style={{ color: 'var(--brand-muted, rgba(255,255,255,0.7))' }}>
              é a minha estreia no mercado
            </div>
          </motion.a>

          <motion.a
            initial={{ opacity: 1, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.62, duration: 0.45 }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.98 }}
            href="/comecar?intent=upgrade"
            data-testid="entry-choice-troca"
            className="group text-left rounded-2xl p-7 border transition-colors"
            style={{
              background: 'var(--brand-surface, rgba(60,200,181,0.08))',
              borderColor: 'var(--brand-line, rgba(60,200,181,0.32))',
            }}
          >
            <div className="flex items-center justify-between">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--brand-surface-2, rgba(60,200,181,0.18))' }}>
                <Repeat size={22} style={{ color: 'var(--brand-good-text, #3CC8B5)' }} />
              </div>
              <ArrowRight size={18} className="opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ color: 'var(--brand-good-text, #3CC8B5)' }} />
            </div>
            <div className="mt-5 text-xl font-semibold text-white">Trocar de casa</div>
            <div className="mt-1 text-sm" style={{ color: 'var(--brand-muted, rgba(255,255,255,0.7))' }}>
              já tenho casa, quero outra
            </div>
          </motion.a>
        </div>
      </main>

      {/* Rodapé de acessos discretos */}
      <motion.footer
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="pb-8 px-6 flex flex-col items-center gap-3"
      >
        <div className="flex flex-wrap justify-center items-center gap-6 text-sm" style={{ color: 'var(--brand-muted, rgba(255,255,255,0.7))' }}>
          <Link to="/login" data-testid="entry-link-login" className="hover:text-white transition-colors">Entrar</Link>
          <span style={{ color: 'var(--brand-line-strong, rgba(255,255,255,0.15))' }}>·</span>
          <Link to="/precos-agentes" data-testid="entry-link-agent" className="hover:text-white transition-colors">Sou mediador</Link>
          <span style={{ color: 'var(--brand-line-strong, rgba(255,255,255,0.15))' }}>·</span>
          <Link to="/intermediary/login" data-testid="entry-link-intermediary" className="hover:text-white transition-colors">Sou intermediário</Link>
        </div>
        <Link to="/sobre" data-testid="entry-link-sobre"
          className="text-xs hover:text-white transition-colors"
          style={{ color: 'var(--brand-muted, rgba(255,255,255,0.7))' }}>
          O que é o miD?
        </Link>
      </motion.footer>
    </div>
    </>
  );
}

/* ─── Ecrã de arranque (manual variante A) ─────────────────────
   Lockup grande + tagline + barra de progresso. Desaparece sozinho
   ao fim de `duration` ou ao toque. */
function Splash({ duration = 2200, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, duration);
    return () => clearTimeout(t);
  }, [duration, onDone]);

  return (
    <motion.div
      className={`fixed inset-0 z-[60] flex flex-col items-center justify-center overflow-hidden cursor-pointer${NIGHT}`}
      style={{
        background: 'var(--brand-paper, radial-gradient(ellipse 80% 60% at 50% -10%, rgba(167,139,217,0.14), transparent), #0a0c14)',
        fontFamily: "var(--brand-font-body, 'Geist', 'Inter', system-ui, sans-serif)",
      }}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
      onClick={onDone}
      role="button"
      aria-label="Entrar no miD"
      data-testid="entry-splash"
    >
      {/* Glows do manual (roxo + teal) */}
      <div style={{ display: NEW_BRAND ? 'none' : undefined, position: 'absolute', width: 360, height: 360, borderRadius: '50%', background: 'var(--brand-primary, #4B2E8C)', opacity: 0.4, filter: 'blur(90px)', left: '10%', top: '6%' }} />
      <div style={{ display: NEW_BRAND ? 'none' : undefined, position: 'absolute', width: 320, height: 320, borderRadius: '50%', background: 'var(--brand-good-text, #0E6B60)', opacity: 0.32, filter: 'blur(90px)', right: '10%', bottom: '12%' }} />

      <motion.div
        initial={{ opacity: 1, y: 10, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex flex-col items-center gap-7 px-6"
      >
        <MidLockup
          variant="vivo"
          fontSize={100}
          color="#FFFFFF"
          dColor="#A78BD9"
          data-testid="splash-lockup"
        />
        <div className="text-center">
          <div className="text-lg md:text-xl font-medium text-white leading-snug">
            A identidade digital<br />
            de <span style={{ color: 'var(--brand-good-text, #3CC8B5)' }}>quem procura casa</span>.
          </div>
          <div
            style={{
              marginTop: 12,
              fontSize: 11,
              letterSpacing: '0.26em',
              textTransform: 'uppercase',
              color: 'var(--brand-muted, rgba(255,255,255,0.7))',
              fontWeight: 500,
            }}
          >
            Mediador Imobiliário Digital
          </div>
        </div>
      </motion.div>

      {/* Barra de progresso */}
      <div
        style={{ position: 'absolute', bottom: 56, width: 150, height: 3, borderRadius: NEW_BRAND ? 0 : 3, background: 'var(--brand-line, rgba(255,255,255,0.10))', overflow: 'hidden' }}
        data-testid="splash-progress"
      >
        <motion.div
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ duration: duration / 1000, ease: 'easeInOut' }}
          style={{ height: '100%', borderRadius: NEW_BRAND ? 0 : 3, background: 'var(--brand-primary, linear-gradient(90deg,#3CC8B5,#A78BD9))' }}
        />
      </div>
    </motion.div>
  );
}
